/**
 * Utility class for tracking game events with Google Analytics
 */
export class AnalyticsManager {
  constructor() {
    // Initialize analytics properties
    this.sessionStartTime = Date.now();
    this.frameRates = []; // Only used for analytics reporting
    this.frameRateTrackingEnabled = false;

    // Check if running on localhost
    this.isLocalhost = this.checkIfLocalhost();

    // Disable analytics on localhost
    if (this.isLocalhost) {
      console.log("Running on localhost - analytics tracking is disabled");
      this.isAvailable = false;
      return;
    }

    // Check if Google Analytics is available
    this.isAvailable =
      typeof window !== "undefined" && typeof window.gtag === "function";

    if (!this.isAvailable) {
      console.warn(
        "Google Analytics is not available - events will not be tracked"
      );
    }
  }

  /**
   * Check if the application is running on localhost
   * @returns {boolean} True if running on localhost
   */
  checkIfLocalhost() {
    if (typeof window === "undefined" || !window.location) {
      return false;
    }

    const hostname = window.location.hostname;
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.endsWith(".local")
    );
  }

  /**
   * Track a game event
   * @param {string} eventName - Name of the event
   * @param {Object} eventParams - Additional parameters for the event
   */
  trackEvent(eventName, eventParams = {}) {
    // If running on localhost, log to console but don't send to Google Analytics
    if (this.isLocalhost) {
      console.log(
        `[Analytics Disabled on Localhost] Event: ${eventName}`,
        eventParams
      );
      return;
    }

    // Return if Google Analytics is not available
    if (!this.isAvailable) return;

    try {
      window.gtag("event", eventName, eventParams);
    } catch (error) {
      console.error("Error tracking event:", error);
    }
  }

  /**
   * Track when a game session starts
   */
  trackGameStart() {
    // Reset session metrics
    this.sessionStartTime = Date.now();
    this.frameRates = [];

    this.trackEvent("game_start");
  }

  /**
   * Track when a game session ends
   * @param {string} reason - Reason for game ending (obstacle, fall, blocked, etc.)
   * @param {number} distance - Distance traveled in meters
   * @param {number} timeMs - Time played in milliseconds
   * @param {string} sceneName - Name of the scene where the player died
   * @param {number} score - Final player score
   */
  trackGameOver(reason, distance, timeMs, sceneName, score) {
    const sessionData = {
      reason: reason,
      distance: Math.floor(distance * 10), // Convert to meters (same as display)
      time_seconds: Math.floor(timeMs / 1000),
      time_formatted: this.formatTime(timeMs),
      scene_name: sceneName,
      score: score || 0, // Include the score, defaulting to 0 if not provided
    };

    // Add average frame rate if tracking was enabled
    if (this.frameRateTrackingEnabled && this.frameRates.length > 0) {
      const avgFrameRate =
        this.frameRates.reduce((a, b) => a + b, 0) / this.frameRates.length;
      sessionData.avg_frame_rate = Math.round(avgFrameRate);
    }

    this.trackEvent("game_over", sessionData);
  }

  /**
   * Track milestone distance achievements
   * @param {number} milestone - Distance milestone in meters
   */
  trackDistanceMilestone(milestone) {
    const sessionTime = Math.floor((Date.now() - this.sessionStartTime) / 1000);

    this.trackEvent("distance_milestone", {
      milestone: milestone,
      time_to_milestone: sessionTime,
    });
  }

  /**
   * Enable frame rate tracking
   */
  enableFrameRateTracking() {
    // Don't enable frame rate tracking on localhost
    if (this.isLocalhost) {
      console.log(
        "[Analytics Disabled on Localhost] Frame rate tracking not enabled"
      );
      return;
    }

    this.frameRateTrackingEnabled = true;
  }

  /**
   * Track a frame rate sample
   * @param {number} fps - Frames per second
   */
  trackFrameRate(fps) {
    // Skip if running on localhost
    if (this.isLocalhost) return;

    if (this.frameRateTrackingEnabled) {
      this.frameRates.push(fps);

      // Keep the array at a reasonable size
      if (this.frameRates.length > 100) {
        this.frameRates.shift();
      }
    }
  }

  /**
   * Format time in MM:SS.MS format for analytics
   * @param {number} timeMs - Time in milliseconds
   * @returns {string} Formatted time string
   */
  formatTime(timeMs) {
    const totalSeconds = timeMs / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.floor((totalSeconds * 100) % 100);

    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}.${milliseconds.toString().padStart(2, "0")}`;
  }
}
